import React, {useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {useInjection} from "inversify-react";
import {AppController} from "@dropDesk/presentation/app/app.controller";
import {UserEntity} from "@dropDesk/domain/entities/user/user.entity";
import DropDeskLoading from "@dropDesk/presentation/components/loadings/loading_dropdesk";
import {RoutesEnum} from "@dropDesk/domain/entities/routes/routes_enum";
import {getActiveRoute} from "@dropDesk/utils/helpers/common";
import {onAuthStateChanged} from "firebase/auth";
import {auth} from "@dropDesk/data/clients/firebase.client";
import {UserRole} from "@dropDesk/domain/entities/user/user_enum";
import History from "@dropDesk/presentation/routes/history";
import {Helmet} from "react-helmet";
import {ChatController} from "@dropDesk/presentation/pages/chat/controller/chat.controller";
import {observer} from "mobx-react";
import {ConstantsKeys} from "@dropDesk/domain/entities/constants/constants_keys";
import {InternalChatController} from "@dropDesk/presentation/pages/chat/controller/internal_chat.controller";

export const AuthContext = React.createContext<UserEntity | null | undefined>(null);

export const AuthGuardProvider = observer(({children}: any) => {

    const [pending, setPending] = useState(true);
    const appController = useInjection(AppController);
    const chatController: ChatController = useInjection(ChatController);
    const internalChatController: InternalChatController = useInjection(InternalChatController);
    const navigate = useNavigate();
    const activeRoute = getActiveRoute();
    const {search} = useLocation();
    History.navigate = useNavigate();

    const getQueryRef = (): string | null => {
        return new URLSearchParams(search).get("ref");
    }

    const resolveUser = async () => {
        const observer = onAuthStateChanged(auth, async function (user) {
            try {
                appController.getLocalTheme();
                await appController.initialize(getQueryRef());

                if (!appController.user) {
                    await appController.getCurrentUser();
                }

                if (appController.user) {
                    if (
                        activeRoute === RoutesEnum.login ||
                        activeRoute === RoutesEnum.companyCreate ||
                        activeRoute === RoutesEnum.loginDefault
                    ) {
                        navigate(appController.user.role === UserRole.attendant ? RoutesEnum.dashboard : RoutesEnum.tickets, {replace: true});
                    }
                }

                observer();
                setPending(false);
            } catch (e) {
                observer();
                setPending(false);
            }
        });
    }
    const detectMobilePage = () => {
        if (window.innerWidth <= ConstantsKeys.widthMobile || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Windows Phone|Kindle|Silk|Mobile|webOS|PlayBook|Samsung|Nokia|HTC|LG|Sony|Motorola|Huawei|Pixel|Mi|Lenovo|Meizu|OnePlus|ZTE|Bada|Tizen|Firefox OS|Symbian|Chrome Mobile|Safari Mobile|UCBrowser/i
            .test(navigator.userAgent)) {
            if (process.env.MODE === 'development' || process.env.MODE === 'uat') {
                window.location.href = process.env.DROPDESK_APP_URL as string;
            }
        }
    }

    useEffect(() => {
        detectMobilePage();
        resolveUser().then();
    }, []);

    if (pending) {
        return (
            <div
                style={{
                    display: "flex",
                    flex: 1,
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: appController.theme.colorScheme.delicateBackground,
                    height: "100%",
                }}>
                <DropDeskLoading
                    height={250}
                    description={appController.loadingMessage ?? "signIn.default"}
                />
            </div>
        );
    }

    const hasNotification = chatController.countNotificationDocumentHead > 0 || internalChatController.countNotificationDocumentHead > 0;
    const getCountNotifications = (): string => {
        return (chatController.countNotificationDocumentHead + internalChatController.countNotificationDocumentHead).toString();
    }

    return (
        <AuthContext.Provider
            value={
                appController.user
            }>
            <Helmet defer={false}>
                <meta charSet="utf-8"/>
                {hasNotification &&
                    <title>({getCountNotifications()}) DropDesk</title>}
                {!hasNotification && <title>DropDesk</title>}
            </Helmet>
            {children}
        </AuthContext.Provider>
    );
});
